import { animated } from '@react-spring/web';
import { useEffect, useState } from 'react';
import type { PropsWithChildren } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { useCopyToClipboard } from '#src/hooks/useCopyToClipboard.ts';

const CopyButtonContainer = styled.div`
  position: relative;
`;
const CopyButton = styled(animated.button)<{
  $alignRight?: boolean;
}>`
  cursor: pointer;
  color: var(--c-blue-400);
  font-weight: bold;
  white-space: nowrap;
  &[data-absolute] {
    position: absolute;
    margin: auto;
    top: 0;
    bottom: 0;
    ${({ $alignRight }) => ($alignRight ? 'right: 0;' : 'left: 0')}
  }
`;
type Props = {
  textToCopy: string;
  alignRight?: boolean;
  onClick?: () => void;
  absolute?: boolean;
  className?: string;
};

export const CopyToClipboardButton = ({
  textToCopy,
  alignRight,
  onClick,
  children,
  absolute = false,
  className,
}: PropsWithChildren<Props>) => {
  const [copied, setCopied] = useState(false);
  const copyToClipboard = useCopyToClipboard();

  const onCopy = () => {
    copyToClipboard(textToCopy, () => {
      setCopied(true);
    });
    if (onClick) onClick();
  };

  useEffect(() => {
    if (!copied) return () => {};
    const timer = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(timer);
  }, [copied]);

  return (
    <CopyButtonContainer className={className}>
      <CopyButton
        $alignRight={alignRight}
        onClick={onCopy}
        data-absolute={absolute || undefined}
      >
        {copied ? (
          <FormattedMessage defaultMessage="Copied!" />
        ) : (
          children || <FormattedMessage defaultMessage="Copy" />
        )}
      </CopyButton>
    </CopyButtonContainer>
  );
};
